import React from "react";
import { InfiniteScrollComponent } from "../../../library/components/components";
import { sortByProperty } from "../../functions/sort-by-property";
import { generateIndustryLabel } from "../../functions/industry-label";
import { parseFiltersFromUrl, writeFiltersToUrl, subscribePopstate, arraysShallowEqual, normalizeArray } from "../../functions/filter-url-state";
import SortBar from "./sort-bar";
import GallerySearchBar from "./gallery-search-bar";
import SearchFiltersBar from "./search-filters-bar";
import LoadingIndicator from "../loading/loading-indicator";

function GetDocTypeLabel(docType) {
    switch (docType) {
        case 'frame': return 'Frames';
        case 'fabric': return 'Fabrics';
        case 'custom_option': return 'Custom Options';
        default: return docType || '';
    }
}

// URL ↔ filter state mapping for the Search Results page.
// URL keys deliberately short — see docs/filter-url-state-plan.md.
//
// Plurals for multi-value (CSV) keys are deliberate: `types`, `categories`,
// `collections` avoid colliding with the singular params already declared by
// SearchController.Results (`docType`, `category`, `collection`) which would
// otherwise cause the server to pre-narrow Model.Items and collapse the
// derived filter options.
var URL_SCHEMA = {
    types: 'array', industry: 'array', categories: 'array', collections: 'array',
    status: 'array', color: 'array', pattern: 'array', grade: 'array',
    priceMin: 'number', priceMax: 'number',
    widthMin: 'number', widthMax: 'number',
    heightMin: 'number', heightMax: 'number',
    depthMin: 'number', depthMax: 'number',
    armHeightMin: 'number', armHeightMax: 'number',
    seatHeightMin: 'number', seatHeightMax: 'number',
    sort: 'array', find: 'string'
};
var DIMENSION_KEYS = ['width', 'height', 'depth', 'armHeight', 'seatHeight'];

// Default sort for Search Results — also rendered through SortBar's defaultSort prop.
// Suppressed from the URL by syncUrl when the current sort still matches it, so a
// clean URL stays clean.
var DEFAULT_SORT = ["docType|DESC", "name|ASC"];

// Hardcoded canonical values for filters whose dropdown options aren't supplied by
// the server. Used for case-insensitive normalization of URL-pasted values.
var INDUSTRY_CANONICAL = ['Residential', 'Commercial', 'Senior Living'];
var STATUS_CANONICAL = ['New Items'];

// Case-insensitively normalize parsed URL state against the server-supplied filter
// option lists (and hardcoded canonical lists for industry/status). Mutates and
// returns `parsed` so downstream chip/dropdown lookups (which are case-sensitive
// indexOf) match the canonical case.
function normalizeUrlState(parsed, serverFilters) {
    if (!parsed) return parsed;
    var f = serverFilters || {};
    if (parsed.types)       parsed.types       = normalizeArray(parsed.types,       f.docTypes);
    if (parsed.categories)  parsed.categories  = normalizeArray(parsed.categories,  f.categories);
    if (parsed.collections) parsed.collections = normalizeArray(parsed.collections, f.collections);
    if (parsed.color)       parsed.color       = normalizeArray(parsed.color,       f.colors);
    if (parsed.pattern)     parsed.pattern     = normalizeArray(parsed.pattern,     f.patterns);
    if (parsed.grade)       parsed.grade       = normalizeArray(parsed.grade,       f.grades);
    if (parsed.industry)    parsed.industry    = normalizeArray(parsed.industry,    INDUSTRY_CANONICAL);
    if (parsed.status)      parsed.status      = normalizeArray(parsed.status,      STATUS_CANONICAL);
    return parsed;
}

// Convert URL-parsed object → SearchFiltersBar state shape.
function urlToFilterBarState(u) {
    if (!u) return null;
    var hasAny = false;
    var out = {
        docTypes: [], industry: [], categories: [], collections: [],
        patterns: [], colors: [], grades: [], quick: [],
        priceRange: {}, dimensions: {}
    };
    if (u.types) { out.docTypes = u.types; hasAny = true; }
    if (u.industry) { out.industry = u.industry; hasAny = true; }
    if (u.categories) { out.categories = u.categories; hasAny = true; }
    if (u.collections) { out.collections = u.collections; hasAny = true; }
    if (u.status) { out.quick = u.status; hasAny = true; }
    if (u.color) { out.colors = u.color; hasAny = true; }
    if (u.pattern) { out.patterns = u.pattern; hasAny = true; }
    if (u.grade) { out.grades = u.grade; hasAny = true; }
    if (u.priceMin != null) { out.priceRange.greaterThan = u.priceMin; hasAny = true; }
    if (u.priceMax != null) { out.priceRange.lessThan = u.priceMax; hasAny = true; }
    for (var i = 0; i < DIMENSION_KEYS.length; i++) {
        var dk = DIMENSION_KEYS[i];
        if (u[dk + 'Min'] != null) { out.dimensions[dk + 'Min'] = u[dk + 'Min']; hasAny = true; }
        if (u[dk + 'Max'] != null) { out.dimensions[dk + 'Max'] = u[dk + 'Max']; hasAny = true; }
    }
    return hasAny ? out : null;
}

// Convert SearchFiltersBar state → URL-shape object for writeFiltersToUrl.
function filterBarStateToUrl(s) {
    if (!s) return {};
    var out = {
        types: s.docTypes, industry: s.industry, categories: s.categories,
        collections: s.collections, status: s.quick, color: s.colors,
        pattern: s.patterns, grade: s.grades
    };
    if (s.priceRange) {
        if (s.priceRange.greaterThan != null) out.priceMin = s.priceRange.greaterThan;
        if (s.priceRange.lessThan != null) out.priceMax = s.priceRange.lessThan;
    }
    if (s.dimensions) {
        for (var i = 0; i < DIMENSION_KEYS.length; i++) {
            var dk = DIMENSION_KEYS[i];
            if (s.dimensions[dk + 'Min'] != null) out[dk + 'Min'] = s.dimensions[dk + 'Min'];
            if (s.dimensions[dk + 'Max'] != null) out[dk + 'Max'] = s.dimensions[dk + 'Max'];
        }
    }
    return out;
}

// Shared filter predicate — same logic used by onFiltersChange and applyUrlState.
function productMatchesFilters(product, f) {
    if (f.docTypes && f.docTypes.length) {
        if (f.docTypes.indexOf(GetDocTypeLabel(product.docType)) === -1) return false;
    }
    if (f.categories && f.categories.length) {
        if (f.categories.indexOf(product.category || "") === -1) return false;
    }
    if (f.collections && f.collections.length) {
        if (f.collections.indexOf(product.collection || "") === -1) return false;
    }
    if (f.patterns && f.patterns.length) {
        if (f.patterns.indexOf(product.patternGroup || "") === -1) return false;
    }
    if (f.colors && f.colors.length) {
        if (f.colors.indexOf(product.colorGroup || "") === -1) return false;
    }
    if (f.grades && f.grades.length) {
        if (f.grades.indexOf(product.grade || "") === -1) return false;
    }
    if (f.quick && f.quick.length) {
        var okQuick = false;
        for (var q = 0; q < f.quick.length; q++) {
            if (f.quick[q] === "New Items" && product.badge === "New") { okQuick = true; break; }
        }
        if (!okQuick) return false;
    }
    if (f.industry && f.industry.length) {
        var okIndustry = false;
        var badges = product.badges || [];
        for (var m = 0; m < f.industry.length; m++) {
            if (badges.indexOf(f.industry[m]) !== -1) { okIndustry = true; break; }
        }
        if (!okIndustry) return false;
    }
    if (f.priceRange) {
        var pGt = f.priceRange.greaterThan;
        var pLt = f.priceRange.lessThan;
        if (pGt != null || pLt != null) {
            var pv = product.priceValue;
            if (!pv) return false;
            if (pGt != null && pv < pGt) return false;
            if (pLt != null && pv > pLt) return false;
        }
    }
    if (f.dimensions) {
        for (var di = 0; di < DIMENSION_KEYS.length; di++) {
            var dk = DIMENSION_KEYS[di];
            var dMin = f.dimensions[dk + "Min"];
            var dMax = f.dimensions[dk + "Max"];
            if (dMin != null || dMax != null) {
                var val = product[dk];
                if (val == null) return false;
                if (dMin != null && val < dMin) return false;
                if (dMax != null && val > dMax) return false;
            }
        }
    }
    return true;
}

class SearchGalleryItem extends React.Component {
    constructor(props) {
        super(props);
        this.state = { imgError: false };
        this.imgRef = null;
    }

    componentDidMount() {
        // Catch images that failed before React hydration attached onError
        if (this.imgRef && this.imgRef.complete && this.imgRef.naturalHeight === 0) {
            this.handleImageError();
        }
    }

    handleImageError = () => {
        this.setState({ imgError: true });
    };

    getImagePath(docType) {
        if (docType === 'fabric') return '/fabrics-leathers/thumbnail';
        if (docType === 'custom_option') return '/fabrics-leathers';
        return '/styles/thumbnail';
    }

    render() {
        const { item, baseImageUrl } = this.props;
        const { imgError } = this.state;
        let imgName = item.image ? item.image.replace(/^[\\/]+/, '') : null;
        let src = imgName ? baseImageUrl + this.getImagePath(item.docType) + "/" + imgName : null;
        const showLike = (item.docType === 'frame' || item.docType === 'fabric') && this.props.onLike;
        return (
            <gallery-item>
                {item.badge && <div className="gallery-item-badge">{item.badge}</div>}
                <a href={item.url} className="image-container">
                    {src && !imgError ? (
                        <img ref={el => this.imgRef = el} src={src} alt={item.name} className="lazy-image gallery-item-image" onError={this.handleImageError} />
                    ) : (
                        <div className="gallery-item-placeholder"><image-placeholder></image-placeholder></div>
                    )}
                </a>
                <a href={item.url} className="gallery-item-info">
                    {item.docType === 'fabric' ? (
                        <>
                            {item.pattern && <span className="gallery-item-sku">{item.pattern}</span>}
                            <span className="gallery-item-title">{item.description || item.name}</span>
                            {item.grade && <span className="gallery-item-grade">GRADE: {item.grade}</span>}
                        </>
                    ) : item.docType === 'frame' ? (
                        <>
                            {item.collection && <span className="gallery-item-collection">{item.collection}</span>}
                            <span className="gallery-item-title">{item.name}</span>
                            {item.extDescription && <span className="gallery-item-description">{item.extDescription}</span>}
                            <span className="gallery-item-sku">{item.sku}</span>
                            {generateIndustryLabel(item.industries) && <span className="gallery-item-grade">{generateIndustryLabel(item.industries)}</span>}
                            {item.price && this.props.isSignedIn && (
                                item.clearancePrice ? (
                                    <span className="gallery-item-price">
                                        <span className="price-original">From {item.price}</span>
                                        <span className="price-clearance">{item.clearancePrice}</span>
                                    </span>
                                ) : (
                                    <span className="gallery-item-price">From {item.price} <span className="price-msrp">(MSRP)</span></span>
                                )
                            )}
                        </>
                    ) : (
                        <>
                            {item.collection && <span className="gallery-item-collection">{item.collection}</span>}
                            <span className="gallery-item-title">{item.name}</span>
                            <span className="gallery-item-sku">{item.sku}</span>
                            {item.docTypeLabel && <span className="gallery-item-grade">{item.docTypeLabel}</span>}
                            {item.price && this.props.isSignedIn && <span className="gallery-item-price">{item.price} <span className="price-msrp">(MSRP)</span></span>}
                        </>
                    )}
                    {showLike && (
                        <div className="gallery-item-actions">
                            <div className="gallery-item-like">
                                <img src="/images/icons/heart-icon.png" alt="Like" onClick={(e) => this.props.onLike(e, item)} />
                            </div>
                            {/* Sample cart from search results (ClickUp 1dfdcycujg8,
                                2026-10-20). canSample comes from the Razor projection
                                (Results.cshtml) — true only for fabric/leather SKUs in
                                the cart's eligibility catalog (trim + disabled excluded),
                                mirroring the fabric gallery tile. Same event the
                                fabric-cart-counter.js listener handles sitewide. */}
                            {item.canSample && (
                            <div className="gallery-item-cart" title="Add to Sample Cart">
                                <img src="/images/icons/cart-icon.svg" alt="Add to Sample Cart" onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    document.dispatchEvent(new CustomEvent('post-fabric-cart-item', {
                                        detail: { sku: item.sku, name: item.name || item.sku }
                                    }));
                                }} />
                            </div>
                            )}
                        </div>
                    )}
                </a>
            </gallery-item>
        );
    }
}

export class SearchGalleryComponent extends InfiniteScrollComponent {
    constructor(props) {
        super(props);
        this.yBottom = 500;
        let data = props.data || [];
        let N = 100;
        let restorePage = 1;
        if (typeof window !== 'undefined' && window.sessionStorage) {
            var key = 'search_page_' + window.location.search;
            var saved = sessionStorage.getItem(key);
            if (saved) {
                restorePage = Math.max(1, parseInt(saved, 10) || 1);
                sessionStorage.removeItem(key);
            }
        }

        // Default sort: Name A-Z, then Frames First
        data = sortByProperty(data, "name", "ASC");
        data = sortByProperty(data, "docType", "DESC");

        let pagedData = data.slice(0, N * restorePage);
        this.throttleScroll = false;
        this.state = {
            pagedData: pagedData,
            page: restorePage,
            N: N,
            maxPage: Math.ceil(data.length / N),
            data: data,
            dataCopy: data,
            filteredDataCopy: data,
            sortBy: DEFAULT_SORT,
            searchTerm: '',
            showLoader: false,
            // bumped on popstate to force SearchFiltersBar / SortBar / GallerySearchBar
            // to remount with new initial values
            urlVersion: 0,
            // null until applyUrlState runs in componentDidMount; passed to
            // SearchFiltersBar as initialFilters and tracked here for URL writes
            initialFilterBarState: null
        };
        // Shadow copy of the latest SearchFiltersBar state — needed for URL
        // writes when only sort or find changed.
        this._currentFilterBarState = null;
    }

    componentDidMount() {
        super.componentDidMount();
        if (typeof window !== 'undefined') window.__searchPage = this.state.page;
        this.applyUrlState();
        this._popstateUnsub = subscribePopstate(this.onPopstate);
    }

    componentWillUnmount() {
        if (typeof super.componentWillUnmount === 'function') super.componentWillUnmount();
        if (this._popstateUnsub) {
            this._popstateUnsub();
            this._popstateUnsub = null;
        }
    }

    onPopstate = () => {
        this.applyUrlState({ bumpVersion: true });
    }

    // Read filter/sort/find from URL and apply to data + state.
    // Called on mount, and on popstate (back/forward) with bumpVersion=true
    // so that SearchFiltersBar / SortBar / GallerySearchBar remount.
    applyUrlState = (opts) => {
        var parsed = parseFiltersFromUrl(URL_SCHEMA);
        // Case-insensitively rewrite URL values to their canonical case before
        // anything downstream (chip rendering, dropdown checkbox state, filter
        // predicate matching) uses them. Hand-typed `?types=fabrics` now matches
        // server-side `Fabrics` etc.
        parsed = normalizeUrlState(parsed, this.props.filters);
        var filterState = urlToFilterBarState(parsed);
        var sort = parsed.sort || null;
        var find = parsed.find || '';

        // Nothing to do if URL carries no filter/sort/find params
        if (!filterState && !sort && !find) {
            if (opts && opts.bumpVersion) {
                this.setState({ urlVersion: (this.state.urlVersion || 0) + 1 });
            }
            return;
        }

        this._currentFilterBarState = filterState;

        var filtered = (this.state.dataCopy || []).slice();
        if (filterState) {
            filtered = filtered.filter(function (p) { return productMatchesFilters(p, filterState); });
        }
        var sortBy = sort || this.state.sortBy || [];
        for (var i = sortBy.length - 1; i >= 0; i--) {
            var params = sortBy[i].split('|');
            filtered = sortByProperty(filtered, params[0], params[1]);
        }
        var filteredDataCopy = filtered.slice();
        if (find) {
            var self = this;
            filtered = filtered.filter(function (p) { return self.matchesSearch(p, find); });
        }

        var paged = filtered.slice(0, this.state.N);
        var nextState = {
            pagedData: paged,
            data: filtered,
            filteredDataCopy: filteredDataCopy,
            page: 1,
            maxPage: Math.ceil(filtered.length / this.state.N),
            sortBy: sortBy,
            searchTerm: find,
            initialFilterBarState: filterState
        };
        if (opts && opts.bumpVersion) {
            nextState.urlVersion = (this.state.urlVersion || 0) + 1;
        } else if (filterState || find) {
            // Initial mount: bump version once so SearchFiltersBar / GallerySearchBar
            // (which read initial values in their constructor only) pick up the URL-
            // driven state instead of empty defaults.
            nextState.urlVersion = (this.state.urlVersion || 0) + 1;
        }
        this.setState(nextState);
    }

    // Push the current filter + sort + find combo to the URL.
    syncUrl = (overrides) => {
        var sortBy = (overrides && overrides.sortBy) ? overrides.sortBy : (this.state.sortBy || []);
        var searchTerm = (overrides && typeof overrides.searchTerm === 'string')
            ? overrides.searchTerm
            : (this.state.searchTerm || '');
        var filterBarState = (overrides && overrides.filterBarState !== undefined)
            ? overrides.filterBarState
            : this._currentFilterBarState;
        var urlState = filterBarStateToUrl(filterBarState);
        // Only emit `sort` when the active sort differs from the default — keeps
        // a clean URL clean when the user hasn't touched the sort dropdown.
        if (sortBy && sortBy.length > 0 && !arraysShallowEqual(sortBy, DEFAULT_SORT)) {
            urlState.sort = sortBy;
        }
        if (searchTerm) urlState.find = searchTerm;
        writeFiltersToUrl(urlState, URL_SCHEMA);
        this.pruneStaleSearchPageStorage();
    }

    // Clear `search_page_*` sessionStorage entries that no longer match the
    // current URL. Defensive housekeeping — restorePage logic in the constructor
    // is keyed on full `window.location.search`, so when the URL changes via a
    // filter / sort / find toggle, prior keys become orphans. Cheap to run.
    pruneStaleSearchPageStorage = () => {
        if (typeof window === 'undefined' || !window.sessionStorage) return;
        var currentKey = 'search_page_' + window.location.search;
        var toRemove = [];
        for (var i = 0; i < window.sessionStorage.length; i++) {
            var k = window.sessionStorage.key(i);
            if (k && k.indexOf('search_page_') === 0 && k !== currentKey) toRemove.push(k);
        }
        for (var j = 0; j < toRemove.length; j++) {
            window.sessionStorage.removeItem(toRemove[j]);
        }
    }

    onLike = (e, item) => {
        e.preventDefault();
        e.stopPropagation();
        if (item.docType !== 'frame' && item.docType !== 'fabric') return;
        const itemType = item.docType === 'fabric' ? 'Fabric' : 'Frame';
        const imgName = item.image ? item.image.replace(/^[\\/]+/, '') : '';
        const path = item.docType === 'fabric' ? '/fabrics-leathers' : '/styles/hires';
        const image = imgName ? this.props.baseImageUrl + path + '/' + imgName + '?w=500' : '';
        document.dispatchEvent(new CustomEvent('favorite-add', {
            detail: {
                itemType: itemType,
                sku: item.sku,
                name: item.name,
                image: image,
                collection: item.collection || '',
                link: item.url || window.location.pathname,
                price: item.price || ''
            }
        }));
    }

    componentDidUpdate() {
        if (typeof window !== 'undefined') window.__searchPage = this.state.page;
    }

    onSortChange = (sortBy) => {
        // sortBy is an array of sort values, e.g. ["docType|DESC", "name|ASC"]
        let sorts = Array.isArray(sortBy) ? sortBy : (sortBy ? [sortBy] : []);
        let sortedData = [...this.state.dataCopy];

        // Re-apply filter chips so a sort change doesn't lose them
        if (this._currentFilterBarState) {
            sortedData = sortedData.filter(p => productMatchesFilters(p, this._currentFilterBarState));
        }
        if (this.state.searchTerm && this.state.searchTerm.trim() !== '') {
            sortedData = sortedData.filter(p => this.matchesSearch(p, this.state.searchTerm));
        }

        // Apply sorts in reverse order (last applied = primary sort)
        for (let i = sorts.length - 1; i >= 0; i--) {
            let params = sorts[i].split("|");
            sortedData = sortByProperty(sortedData, params[0], params[1]);
        }

        let pagedData = sortedData.slice(0, this.state.N);
        this.setState({
            data: sortedData,
            pagedData: pagedData,
            page: 1,
            maxPage: Math.ceil(sortedData.length / this.state.N),
            sortBy: sorts
        });
        this.syncUrl({ sortBy: sorts });
    }

    // Clear All chip — reset filters AND sort so the URL goes clean.
    // SearchFiltersBar fires this in lieu of onFiltersChange when its parent
    // provides an onClearAll handler.
    onClearAll = (clearedFilters) => {
        this._currentFilterBarState = clearedFilters;
        var sorted = (this.state.dataCopy || []).slice();
        for (var i = DEFAULT_SORT.length - 1; i >= 0; i--) {
            var params = DEFAULT_SORT[i].split('|');
            sorted = sortByProperty(sorted, params[0], params[1]);
        }
        var filteredDataCopy = sorted.slice();
        var displayed = sorted;
        if (this.state.searchTerm && this.state.searchTerm.trim() !== '') {
            var self = this;
            displayed = sorted.filter(function (p) { return self.matchesSearch(p, self.state.searchTerm); });
        }
        this.setState({
            pagedData: displayed.slice(0, this.state.N),
            data: displayed,
            filteredDataCopy: filteredDataCopy,
            page: 1,
            maxPage: Math.ceil(displayed.length / this.state.N),
            sortBy: DEFAULT_SORT,
            urlVersion: (this.state.urlVersion || 0) + 1,
            showLoader: false
        });
        this.syncUrl({ filterBarState: clearedFilters, sortBy: DEFAULT_SORT });
    }

    onFiltersChange = (filters) => {
        this._currentFilterBarState = filters;
        var allProducts = this.state.dataCopy;

        var filtered = allProducts.filter(function (p) {
            return productMatchesFilters(p, filters);
        });

        if (this.state.sortBy && this.state.sortBy.length > 0) {
            let sorts = this.state.sortBy;
            for (let i = sorts.length - 1; i >= 0; i--) {
                let params = sorts[i].split("|");
                filtered = sortByProperty(filtered, params[0], params[1]);
            }
        }

        var filteredDataCopy = filtered.slice();
        if (this.state.searchTerm && this.state.searchTerm.trim() !== '') {
            filtered = filtered.filter(product => this.matchesSearch(product, this.state.searchTerm));
        }

        var paged = filtered.slice(0, this.state.N);
        this.setState({
            pagedData: paged,
            data: filtered,
            filteredDataCopy: filteredDataCopy,
            page: 1,
            maxPage: Math.ceil(filtered.length / this.state.N),
            showLoader: false
        }, () => {
            this.throttleScroll = false;
        });
        this.syncUrl({ filterBarState: filters });
    }

    onSubmitSearch = (value) => {
        var trimmed = value ? value.trim() : '';
        if (!trimmed) {
            let data = this.state.filteredDataCopy;
            let paged = data.slice(0, this.state.N);
            this.setState({
                pagedData: paged,
                data: data,
                page: 1,
                maxPage: Math.ceil(data.length / this.state.N),
                searchTerm: ''
            });
            this.syncUrl({ searchTerm: '' });
            return;
        }
        let data = this.state.filteredDataCopy;
        let filtered = data.filter(product => this.matchesSearch(product, trimmed));
        let paged = filtered.slice(0, this.state.N);
        this.setState({
            data: filtered,
            pagedData: paged,
            page: 1,
            maxPage: Math.ceil(filtered.length / this.state.N),
            searchTerm: trimmed
        });
        this.syncUrl({ searchTerm: trimmed });
    }

    matchesSearch(product, query) {
        if (!query) return true;
        var q = query.trim().toLowerCase();
        if (!q) return true;

        var sku = (product.sku || "").toLowerCase();
        var name = (product.name || "").toLowerCase();
        var category = (product.category || "").toLowerCase();
        var collection = (product.collection || "").toLowerCase();
        var description = (product.description || "").toLowerCase();

        if (q.length === 1) {
            return sku.startsWith(q) || sku.endsWith("-" + q);
        }
        return sku.indexOf(q) !== -1 ||
            name.indexOf(q) !== -1 ||
            category.indexOf(q) !== -1 ||
            collection.indexOf(q) !== -1 ||
            description.indexOf(q) !== -1;
    }

    rebind = () => {
        let nextPage = this.state.page + 1;
        let pagedData = this.state.data.slice(0, this.state.N * nextPage);
        this.setState({
            pagedData: pagedData,
            page: nextPage,
            showLoader: false
        }, () => {
            this.throttleScroll = false;
        });
    }

    onScroll() {
        super.onScroll();
        if (!this.throttleScroll) {
            this.throttleScroll = true;
            if (this.state.page < this.state.maxPage) {
                this.setState({ showLoader: true }, () => {
                    setTimeout(() => {
                        this.rebind();
                    }, 1000);
                });
            }
        }
    }

    render() {
        const { data, pagedData, showLoader } = this.state;
        const { baseImageUrl, total, filters } = this.props;
        let count = data.length;
        let countLabel = count === 1 ? "item" : "items";
        let displayCount = Math.min(pagedData.length, count);

        return (
            <div>
                <div className="gallery-grid">
                    <div className="gallery-items-container">
                        <div className="filters-header-container">
                            <div className="filters-bar-container">
                                {filters && <SearchFiltersBar
                                    key={"sfb-" + this.state.urlVersion}
                                    onFiltersChange={this.onFiltersChange}
                                    onClearAll={this.onClearAll}
                                    filters={filters}
                                    isSignedIn={this.props.isSignedIn}
                                    initialFilters={this.state.initialFilterBarState}
                                />}
                            </div>
                            <div className="gallery-items-count">
                                <GallerySearchBar
                                    key={"gsb-" + this.state.urlVersion}
                                    onSubmitSearch={this.onSubmitSearch}
                                    initialValue={this.state.searchTerm}
                                />
                                <SortBar
                                    key={"sb-" + this.state.urlVersion}
                                    onSortChange={this.onSortChange}
                                    excludePriceSort={!this.props.isSignedIn}
                                    includeTypeSort={true}
                                    defaultSort={this.state.sortBy}
                                />
                                <span className="count-label">{displayCount}&nbsp;of&nbsp;{count}&nbsp;{countLabel}</span>
                            </div>
                        </div>
                        <div className="gallery-items-list">
                            <shopping-gallery>
                                {pagedData.map((item) => (
                                    <SearchGalleryItem key={item.id} item={item} baseImageUrl={baseImageUrl} isSignedIn={this.props.isSignedIn} onLike={this.onLike} />
                                ))}
                            </shopping-gallery>
                            {count < 1 &&
                                <div className="gallery-no-results">
                                    No results found.
                                </div>
                            }
                        </div>
                    </div>
                </div>
                {showLoader &&
                    <LoadingIndicator />
                }
            </div>
        );
    }
}
